// ---------------------------------------------------------------------------
// Divider Renderer
// ---------------------------------------------------------------------------
// Foundational primitive — publishes the editor `Divider` block as an <hr>
// (or a styled <div> for the gradient variant) instead of a
// "<!-- unknown component: Divider -->" sentinel.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr } from "../escape.js";

type DividerVariant = "solid" | "dashed" | "dotted" | "gradient";

interface DividerProps {
  variant?: DividerVariant | string;
  width?: string;
  margin?: string;
}

const VALID_VARIANTS: ReadonlySet<DividerVariant> = new Set([
  "solid",
  "dashed",
  "dotted",
  "gradient",
]);

// CSS length: number + unit (px/rem/em/%/vh/vw) or the keyword `auto`.
// Reject anything else to keep style="..." attribute safe.
const CSS_LENGTH_RE = /^(auto|0|\d+(\.\d+)?(px|rem|em|%|vh|vw))$/i;

function resolveVariant(value: unknown): DividerVariant {
  if (typeof value === "string" && VALID_VARIANTS.has(value as DividerVariant)) {
    return value as DividerVariant;
  }
  return "solid";
}

function resolveLength(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return CSS_LENGTH_RE.test(trimmed) ? trimmed : fallback;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as DividerProps;

  const variant = resolveVariant(p.variant);
  const width = resolveLength(p.width, "100%");
  const margin = resolveLength(p.margin, "24px");

  const wrapperStyle = `padding: ${attr(margin)} 24px; display: flex; justify-content: center;`;

  if (variant === "gradient") {
    const lineStyle = `height: 1px; width: ${attr(width)}; background: linear-gradient(to right, transparent, var(--ease-color-border, var(--color-border)), transparent);`;
    return `<section class="ease-section ease-divider ease-divider--gradient" style="${wrapperStyle}">
  <div class="ease-divider__line" style="${lineStyle}" role="separator" aria-orientation="horizontal"></div>
</section>`;
  }

  const hrStyle = `border: none; border-top: 1px ${attr(variant)} var(--ease-color-border, var(--color-border)); width: ${attr(width)}; margin: 0;`;
  return `<section class="ease-section ease-divider ease-divider--${attr(variant)}" style="${wrapperStyle}">
  <hr class="ease-divider__rule" style="${hrStyle}">
</section>`;
}

registerRenderer("divider", { render });
